import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  LinearProgress,
  Stack,
  SxProps,
  Theme,
} from "@mui/material";
import moment from "moment";
import { useState } from "react";

import AppButton from "@/common/components/Button/AppButton";
import FileUploader from "@/common/components/Files/FileUploader";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppSignatureCanvas from "@/common/components/Signature/AppSignatureCanvas";
import AppTypography from "@/common/components/Text/AppTypography";
import { FileItem } from "@/common/fileItem";
import { CustomFormikSubProps } from "@/common/helpers/formik";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { fileService } from "@/common/services/file";
import { assertNever } from "@/common/ts/typeHelpers";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  ContractDto,
  CustomerAcknowledgementInputDto,
  CustomerAcknowledgementSettingsDto,
  CustomerContactDataInputDto,
  SubmissionRequirement,
} from "@/core/api/generated/v1";

import VehicleActualStateModal from "../../Vehicle/VehicleActualState/VehicleActualStateModal";
import CustomerContactSelect from "../CustomerContactSelect";

export type CustomerAcknowledgementValues = CustomerAcknowledgementInputDto & {
  isAttachmentFilesUploading?: boolean;
  isAttachmentFilesHaveErrors?: boolean;
};

interface Props {
  formikProps: CustomFormikSubProps<CustomerAcknowledgementValues | null | undefined>;
  contract: ContractDto;
  settings: CustomerAcknowledgementSettingsDto;
  ackShortText: string;
  sx?: SxProps<Theme>;
}

export default function CustomerAcknowledgementInput({
  formikProps,
  contract,
  settings,
  ackShortText,
  sx,
}: Props) {
  const { enqueueSnackbar } = useAppSnackbar();

  const { values, errors, setFieldValue } = formikProps;

  const [isVehicleActualStateModalOpen, setIsVehicleActualStateModalOpen] = useState(false);

  const customerRequest = useApiRequest(
    apiClient.customersApi.apiV1CustomersCustomerIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      customerId: contract.customer?.id || "",
    },
    {
      deps: [contract.customer?.id],
    },
  );
  const customer = customerRequest.data;

  const renderSubmissionRequirementLabel = (req: Nil<SubmissionRequirement>) => {
    if (!req) {
      return null;
    }
    switch (req) {
      case SubmissionRequirement.None:
      case SubmissionRequirement.NotRequired:
        return "";
      case SubmissionRequirement.Optional:
        return "optional";
      case SubmissionRequirement.Required:
        return "required";
      default:
        assertNever(req);
    }
  };

  return (
    <Stack gap={2} sx={{ ...sx }}>
      {/* Privacy policy */}
      <Stack gap={1}>
        <AppTypography variant='subtitle1'>Data privacy</AppTypography>

        <Box>
          Your personal data (signature, ID photo) is stored securely by NexusFleet and will not be
          shared with third parties. Data is retained for the duration of your rental period plus
          any dispute resolution. You may request deletion after vehicle return and final
          inspection.
        </Box>

        {/* <Box>
          <AppLink to='/legal/privacy-policy' target='_blank'>
            TODO: View full privacy policy
          </AppLink>
        </Box> */}

        <FormControl fullWidth>
          <FormControlLabel
            sx={{ width: "fit-content" }}
            label='I understand and consent to data processing as described above'
            control={
              <Checkbox
                checked={values?.isPrivacyPolicyAccepted ?? false}
                onChange={(e) => {
                  setFieldValue("isPrivacyPolicyAccepted", e.target.checked);
                  setFieldValue(
                    "privacyPolicyAcceptedAt",
                    e.target.checked ? moment().format() : null,
                  );
                }}
              />
            }
          />
          {errors?.isPrivacyPolicyAccepted && (
            <FormHelperText error>
              {ValidationHelper.getFormikErrorsAsString(errors?.isPrivacyPolicyAccepted)}
            </FormHelperText>
          )}
          {errors?.privacyPolicyAcceptedAt && (
            <FormHelperText error>
              {ValidationHelper.getFormikErrorsAsString(errors?.privacyPolicyAcceptedAt)}
            </FormHelperText>
          )}
        </FormControl>
      </Stack>

      {/* Acknowledgement */}
      <Stack gap={1}>
        <AppTypography variant='subtitle1'>Acknowledgement</AppTypography>

        <Box>{`"${ackShortText}"`}</Box>

        <Box>
          <AppButton
            variant='outlined'
            color='text'
            startIcon={<AppIcon of='quickView' />}
            onClick={() => setIsVehicleActualStateModalOpen(true)}
          >
            View damage book
          </AppButton>
        </Box>

        {errors?.damageBookViewedAt && (
          <FormHelperText error>
            {ValidationHelper.getFormikErrorsAsString(errors?.damageBookViewedAt)}
          </FormHelperText>
        )}
      </Stack>

      {/* Signature */}
      {(settings.signatureReq === SubmissionRequirement.Optional ||
        settings.signatureReq === SubmissionRequirement.Required) && (
        <Stack gap={1}>
          <AppTypography variant='subtitle1'>
            Signature ({renderSubmissionRequirementLabel(settings.signatureReq)})
          </AppTypography>

          <FormControl fullWidth margin='none'>
            <FormLabel sx={{ mb: 1 }}>Signer</FormLabel>

            {customerRequest.isLoading && <LinearProgress sx={{ mb: 1 }} />}

            {customer && (
              <CustomerContactSelect
                customer={customer}
                value={values?.contact?.contactId}
                isAutoSelectSingleOption
                onChange={(newValue) => {
                  setFieldValue(
                    "contact",
                    newValue?.data
                      ? ({
                          ...newValue?.data,
                          id: newValue?.data?.id,
                          customerId: customer?.id,
                          contactId: newValue?.data?.id,
                        } satisfies CustomerContactDataInputDto)
                      : null!,
                  );
                }}
              />
            )}

            {errors?.contact && (
              <FormHelperText error>
                {ValidationHelper.getFormikErrorsAsString(errors?.contact)}
              </FormHelperText>
            )}
          </FormControl>

          <FormControl fullWidth margin='none'>
            <FormLabel sx={{ mb: 1 }}>Signature</FormLabel>

            <AppSignatureCanvas
              defaultImage={undefined}
              fileName='customer-signature.svg'
              debounceOptions={{
                wait: 1000,
                options: {
                  leading: false,
                  trailing: true,
                },
              }}
              canvasProps={{
                width: 400,
                height: 200,
              }}
              onClear={() => {
                // manually cleanup prev signature image
                fileService.tryDelete({ fileId: values?.signatureImage?.id });

                setFieldValue(`signatureImage`, null);
                setFieldValue("signedAt", null);
              }}
              onChangeDebounced={async (newValue) => {
                console.log("New signature", newValue);

                // upload signature image immediately when changed, to avoid user clicking save signature button
                try {
                  setFieldValue(`isAttachmentFilesUploading`, true);
                  const response =
                    await apiClient.contractsApi.apiV1ContractsContractIdFilesCustomerSignaturesImageUploadPost(
                      {
                        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                        contractId: contract.id || "",
                        file: newValue.file,
                      },
                    );
                  if (response.status == 200 && response.data) {
                    // manually cleanup prev signature image
                    fileService.tryDelete({ fileId: values?.signatureImage?.id });

                    setFieldValue(`signatureImage`, FileItem.toFileUploadedDto(response.data.file));
                    setFieldValue("signedAt", moment().format());
                  }
                } catch (err) {
                  console.error("Failed to upload signature:", err);
                  enqueueSnackbar("Failed to upload signature.", { variant: "error" });
                } finally {
                  setFieldValue(`isAttachmentFilesUploading`, false);
                }
              }}
            />

            {errors?.signatureImage && (
              <FormHelperText error>
                {ValidationHelper.getFormikErrorsAsString(errors?.signatureImage)}
              </FormHelperText>
            )}
            {errors?.signedAt && (
              <FormHelperText error>
                {ValidationHelper.getFormikErrorsAsString(errors?.signedAt)}
              </FormHelperText>
            )}
          </FormControl>
        </Stack>
      )}

      {/* Personal document images */}
      {(settings.signatureReq === SubmissionRequirement.Optional ||
        settings.signatureReq === SubmissionRequirement.Required) && (
        <Stack gap={1}>
          <AppTypography variant='subtitle1'>
            Document photo ({renderSubmissionRequirementLabel(settings.personalDocumentImageReq)})
          </AppTypography>

          <FormControl fullWidth margin='none'>
            <FileUploader
              multiple
              maxFiles={10}
              defaultFiles={FileItem.createManyFrom(values?.personalDocumentImages)}
              uploadFunc={async (requestParameters) => {
                const response =
                  await apiClient.contractsApi.apiV1ContractsContractIdFilesCustomerPersonalDocumentsImageUploadPost(
                    {
                      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                      contractId: contract.id || "",
                      file: requestParameters.file,
                    },
                  );
                if (response.request.status !== 200) {
                  throw response;
                }
                return response.data;
              }}
              onChange={(newFiles) => {
                setFieldValue(`personalDocumentImages`, FileItem.toManyFileUploadedDto(newFiles));
              }}
              onUploadStarted={() => {
                setFieldValue(`isAttachmentFilesUploading`, true);
              }}
              onUploadFinished={() => {
                setFieldValue(`isAttachmentFilesUploading`, false);
              }}
              onValidationStatusChange={(filesValidationStatus) => {
                if (filesValidationStatus)
                  setFieldValue(
                    "isAttachmentFilesHaveErrors",
                    Object.values(filesValidationStatus).some((x) => x === false),
                  );
              }}
            />

            {errors?.personalDocumentImages && (
              <FormHelperText error>
                {ValidationHelper.getFormikErrorsAsString(errors?.personalDocumentImages)}
              </FormHelperText>
            )}
          </FormControl>
        </Stack>
      )}

      <VehicleActualStateModal
        open={isVehicleActualStateModalOpen}
        onClose={() => setIsVehicleActualStateModalOpen(false)}
        subProps={{
          vehicleId: contract.vehicle?.id || "",
        }}
      />
    </Stack>
  );
}
